import React, { createContext, useContext, ReactNode } from 'react';
import { Drawer, useMediaQuery, useTheme } from '@mui/material';
import { useModeTokens } from '@/common/components/ui';

export const DRAWER_WIDTH = 208;

interface AppDrawerContextValue {
  showMiniCollapsed: boolean;
  sidebarTransition: string;
}

const AppDrawerContext = createContext<AppDrawerContextValue>({
  showMiniCollapsed: false,
  sidebarTransition: '420ms cubic-bezier(0.22, 1, 0.36, 1)',
});

export const useAppDrawerState = () => useContext(AppDrawerContext);

export interface AppDrawerProps {
  anchor: 'left' | 'right';
  open: boolean;
  onClose: () => void;
  /** Width when collapsed on desktop. Pass DRAWER_COLLAPSED_WIDTH for the left nav sidebar.
   *  Defaults to 0 (fully hidden when closed). */
  collapsedWidth?: number;
  children: ReactNode;
}

export const AppDrawer: React.FC<AppDrawerProps> = ({
  anchor,
  open,
  onClose,
  collapsedWidth = 0,
  children,
}) => {
  const theme = useTheme();
  const t = useModeTokens();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const sidebarTransition = '420ms cubic-bezier(0.22, 1, 0.36, 1)';
  const showMiniCollapsed = !open && !isSmallScreen && collapsedWidth > 0;
  const drawerWidth = open
    ? DRAWER_WIDTH
    : showMiniCollapsed
      ? collapsedWidth
      : 0;

  const offsetProp =
    anchor === 'left' ? { left: 0, right: 'auto' } : { right: 0, left: 'auto' };

  return (
    <AppDrawerContext.Provider value={{ showMiniCollapsed, sidebarTransition }}>
      <Drawer
        anchor={anchor}
        variant={isSmallScreen ? 'temporary' : 'permanent'}
        open={isSmallScreen ? open : true}
        onClose={onClose}
        hideBackdrop={isSmallScreen}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isSmallScreen ? '100vw' : drawerWidth,
          flexShrink: 0,
          overflow: 'hidden',
          zIndex: (t) => t.zIndex.drawer,
          transition: `width ${sidebarTransition}`,
        }}
        PaperProps={{
          'data-testid': `${anchor}-app-drawer`,
          sx: {
            width: isSmallScreen ? '100vw' : drawerWidth,
            boxSizing: 'border-box',
            overflow: 'hidden',
            ...offsetProp,
            top: '56px',
            bottom: 0,
            height: { xs: 'calc(100% - 56px)', sm: 'auto' },
            bgcolor: t.surfaces.glassDrawer,
            border: isSmallScreen || drawerWidth > 0 ? '1px solid' : 'none',
            borderColor: t.borders.subtle,
            boxShadow: 'none',
            transition: `width ${sidebarTransition}, border-color ${sidebarTransition}, box-shadow ${sidebarTransition}`,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
          },
        }}
      >
        {children}
      </Drawer>
    </AppDrawerContext.Provider>
  );
};
